import { useRef, useState } from "react";
import { Upload, FileCheck2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadOffer } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"];

/** Downscale large photos in the browser so uploads stay well under proxy limits. */
async function compressImage(file) {
  if (!IMAGE_TYPES.includes(file.type) || file.type === "image/heic" || file.size < 1.5 * 1024 * 1024) return file;
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
  return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}

export default function OfferUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (![...IMAGE_TYPES, "application/pdf"].includes(file.type)) {
      toast.error("Please upload a photo (JPG, PNG, WEBP, HEIC) or a PDF.");
      return;
    }
    setBusy(true);
    try {
      const prepared = await compressImage(file);
      if (prepared.size > MAX_BYTES) {
        toast.error("That file is over 10 MB. Try a smaller photo or PDF.");
        return;
      }
      const res = await uploadOffer(prepared);
      onChange({ fileId: res.file_id, filename: res.filename });
      toast.success("Offer uploaded. We’ll review it with your request.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. You can continue without it.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-dashed border-orange-200 bg-orange-50/40 p-5" data-testid="offer-upload">
      <div className="font-semibold text-slate-900">Already have a CarMax, Carvana, or other written offer?</div>
      <p className="text-sm text-slate-600 mt-1">Upload it and give us a chance to beat it. We’ll review your vehicle and let you know whether we can offer more — no obligation.</p>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} data-testid="offer-upload-input" />
      {value?.fileId ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white border border-emerald-200 px-4 py-3" data-testid="offer-upload-done">
          <div className="flex items-center gap-2 text-sm text-slate-800 min-w-0">
            <FileCheck2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="truncate">{value.filename}</span>
          </div>
          <button type="button" onClick={() => onChange(null)} className="text-slate-500 hover:text-slate-900" aria-label="Remove file" data-testid="offer-upload-remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="offer-upload-button"
          className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold text-sm disabled:opacity-70 transition-colors"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload your offer</>}
        </button>
      )}
      <p className="text-xs text-slate-500 mt-3">Beating another offer is not guaranteed. Our purchase offer is subject to vehicle inspection and verification.</p>
    </div>
  );
}
